// Detalle de un pedido: cómo va (se refresca cada 30 s mientras está a la vista), qué se pidió y qué llegó, lo último
// que cambió la distribuidora (decisión D52), el motivo si no se aprobó, cancelar mientras espera y "Volver a pedir".
import {
  formatoFecha,
  formatoGs,
  formatoHora,
  MEDIO_COBRO_ETIQUETA,
  textoCambios,
  textoFaltantes,
} from '../compartido';
import { cx, HojaInferior, Icono, useToast } from '@reparto/ui';
import { type ReactNode, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { alCambiarPedido } from '../datos/chat';
import { Aviso, Boton, ErrorCarga } from '../componentes/base';
import { ComprobantesPedido } from '../componentes/ComprobantesPedido';
import { EsqueletoPedido } from '../componentes/Esqueletos';
import { Pantalla } from '../componentes/Marco';
import { Miniatura } from '../componentes/Miniatura';
import { EstadoPedido, Pasos } from '../componentes/Seguimiento';
import { useVolverAPedir } from '../componentes/VolverAPedir';
import { mensajeDeError } from '../datos/api';
import { olvidarConsultas, useConsulta } from '../datos/consultas';
import { useCuenta, useNegocio } from '../datos/cuenta';
import { cantidadEnUnidad, fechaEntrega, fechaLarga, nombreUnidad, plural, presentacion } from '../datos/formato';
import { cancelarPedido, pedido } from '../datos/pedidos';
import type { ItemDetalle, PedidoDetalle } from '../datos/tipos';

export const REFRESCO_SEGUIMIENTO_MS = 30_000;

export function Pedido() {
  const { id = '' } = useParams();
  const zona = useCuenta((s) => s.perfil?.empresa.zona_horaria);
  const empresa = useCuenta((s) => s.perfil?.empresa.nombre ?? 'La distribuidora');
  const negocio = useNegocio()!;
  const c = useConsulta(`pedido:${id}`, () => pedido(id), { cadaMs: REFRESCO_SEGUIMIENTO_MS });
  const p = c.datos;
  const cortado = p?.seguimiento.tono === 'error' || p?.seguimiento.tono === 'neutro';
  // Todavía no llegó ni se cortó: se dice cuándo llega.
  const pendiente = !!p && !cortado && p.seguimiento.paso < 4;
  const volver = useVolverAPedir();
  const { mostrar } = useToast();
  const navegar = useNavigate();
  const [cancelar, setCancelar] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [errorCancelar, setErrorCancelar] = useState<string | null>(null);
  // Si la distribuidora lo cambia o avanza (decisión D53), se ve sin esperar al próximo refresco.
  const recargar = c.recargar;
  useEffect(() => alCambiarPedido((pid) => pid === id && void recargar()), [id, recargar]);
  /** "Escribir sobre este pedido": la conversación con la distribuidora, con el pedido adjunto. */
  const escribir = (d: PedidoDetalle) =>
    navegar('/mensajes', { state: { pedido: { id: d.id, numero_visible: d.numero_visible } } });

  async function confirmarCancelacion() {
    setCancelando(true);
    setErrorCancelar(null);
    try {
      const d = await cancelarPedido(id);
      c.fijar(d);
      olvidarConsultas(`pedidos:${negocio.id}`);
      setCancelar(false);
      mostrar(`Cancelaste el pedido ${d.numero_visible}`);
    } catch (e) {
      setErrorCancelar(mensajeDeError(e));
      void c.recargar();
    } finally {
      setCancelando(false);
    }
  }

  // Ya confirmado y sin salir: "¿Querés cambiar algo? Escribinos" (con su botón) en vez de "Escribir sobre este pedido".
  const escribinos = !!p && !p.puede_modificar && !!p.chat && p.estado === 'aprobado' && p.seguimiento.paso < 4;

  const repetir = (d: PedidoDetalle) =>
    volver.pedir(
      d.items.map((i) => ({
        id: i.articulo_id,
        nombre: i.nombre,
        codigo: i.codigo,
        unidad: i.unidad,
        unidades_por_bulto: i.unidades_por_bulto,
        precio_bulto: i.precio_bulto,
        bultos: i.bultos,
      })),
      `tu pedido ${d.numero_visible}`,
    );

  return (
    <Pantalla titulo={p ? `Pedido ${p.numero_visible}` : 'Pedido'} subtitulo={negocio.nombre} volver="/pedidos">
      {c.cargando ? (
        <EsqueletoPedido />
      ) : !p ? (
        <ErrorCarga mensaje={c.error} sinRed={c.sinRed} onReintentar={() => void c.recargar()} />
      ) : (
        <div className="flex flex-col gap-4 px-4 py-4">
          {c.error ? (
            <ErrorCarga compacto mensaje={c.error} sinRed={c.sinRed} onReintentar={() => void c.recargar()} />
          ) : null}

          <section aria-label="Cómo va" className="rounded-3xl border border-stone-200 bg-white shadow-sm p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <EstadoPedido s={p.seguimiento} grande />
              <span className="text-[15px] text-stone-600">
                Hecho el {formatoFecha(p.creado_en, zona)} a las {formatoHora(p.creado_en, zona)}
              </span>
            </div>
            <div className="mt-5">
              <Pasos s={p.seguimiento} />
            </div>
            <p className="mt-4 text-[17px] text-stone-800" aria-live="polite">
              {textoDeEstado(p, zona, empresa)}
            </p>
          </section>

          {p.cambio_distribuidora ? (
            <Aviso
              tono="info"
              icono={p.cambio_distribuidora.faltantes?.length ? 'rule' : 'edit_note'}
              titulo={
                p.cambio_distribuidora.faltantes?.length ? 'Tu pedido sale con cambios' : `${empresa} cambió tu pedido`
              }
            >
              <p>
                {p.cambio_distribuidora.faltantes?.length
                  ? textoFaltantes(p.cambio_distribuidora.faltantes)
                  : textoCambios(p.cambio_distribuidora.cambios)}
                .
              </p>
              <p className="mt-1 text-stone-700">
                El {formatoFecha(p.cambio_distribuidora.en, zona)} a las {formatoHora(p.cambio_distribuidora.en, zona)}
                {p.cambio_distribuidora.monto !== null ? (
                  <>
                    {' · Total '}
                    <span className="whitespace-nowrap">{formatoGs(p.cambio_distribuidora.monto)}</span>
                  </>
                ) : null}
                .
              </p>
            </Aviso>
          ) : null}

          {p.estado === 'rechazado' ? (
            <Aviso tono="peligro" titulo={`${empresa} no aprobó el pedido`}>
              {p.motivo_rechazo ? `Motivo: ${p.motivo_rechazo}` : 'Consultá con la distribuidora.'}
            </Aviso>
          ) : null}

          <section
            aria-label="Entrega y pago"
            className="divide-y divide-stone-100 rounded-3xl border border-stone-200 bg-white shadow-sm"
          >
            <Dato icono="event" titulo={pendiente ? 'Te llega' : cortado ? 'Era para el' : 'Llegó el'}>
              {pendiente ? fechaEntrega(p.fecha_entrega, zona) : fechaLarga(p.fecha_entrega)}
            </Dato>
            <Dato icono="payments" titulo={pendiente ? 'Pagás al recibir' : 'Forma de pago'}>
              {MEDIO_COBRO_ETIQUETA[p.cobro]}
            </Dato>
            {p.observaciones ? (
              <Dato icono="chat" titulo="Lo que avisaste">
                {p.observaciones}
              </Dato>
            ) : null}
          </section>

          <section aria-labelledby="articulos">
            <h2 id="articulos" className="mb-3 text-xl font-bold text-stone-900">
              Lo que pediste
            </h2>
            <ul className="divide-y divide-stone-100 rounded-3xl border border-stone-200 bg-white shadow-sm">
              {p.items.map((i) => (
                <FilaItem key={i.articulo_id} i={i} />
              ))}
            </ul>
            <div className="mt-3 rounded-3xl border border-stone-200 bg-white shadow-sm p-4 text-[17px]">
              <div className="flex justify-between py-0.5">
                <span className="text-stone-600">Artículos</span>
                <span className="font-medium tabular-nums">{p.items.length}</span>
              </div>
              <div className="flex justify-between py-0.5">
                <span className="text-stone-600">Bultos</span>
                <span className="font-medium tabular-nums">{p.bultos}</span>
              </div>
              {p.monto !== null ? (
                <div className="mt-2 flex items-baseline justify-between border-t border-stone-200 pt-3">
                  <span className="text-lg font-bold">Total</span>
                  <span className="text-2xl font-bold tabular-nums">{formatoGs(p.monto)}</span>
                </div>
              ) : null}
            </div>
          </section>

          {p.estado === 'aprobado' ? <ComprobantesPedido pedidoId={p.id} /> : null}

          <div className="flex flex-col gap-3 pt-1 pb-2">
            {p.puede_modificar ? (
              <Boton bloque tamano="lg" icono="edit" onClick={() => navegar(`/pedidos/${p.id}/modificar`)}>
                Modificar pedido
              </Boton>
            ) : escribinos ? (
              <Aviso
                tono="marca"
                icono="chat"
                titulo="¿Querés cambiar algo? Escribinos"
                accion={
                  <Boton tamano="sm" className="h-11!" icono="chat" onClick={() => escribir(p)}>
                    Escribinos
                  </Boton>
                }
              >
                {empresa} ya lo confirmó: si necesitás agregar o sacar algo, lo vemos por mensaje.
              </Aviso>
            ) : null}
            {p.items.length ? (
              <Boton
                bloque
                tamano="lg"
                variante={p.puede_modificar ? 'secundario' : 'primario'}
                icono="replay"
                onClick={() => repetir(p)}
              >
                Volver a pedir lo mismo
              </Boton>
            ) : null}
            {p.chat && !escribinos ? (
              <Boton bloque tamano="lg" variante="secundario" icono="forum" onClick={() => escribir(p)}>
                Escribir sobre este pedido
              </Boton>
            ) : null}
            {p.puede_cancelar ? (
              <Boton
                bloque
                tamano="lg"
                variante="peligro"
                icono="cancel"
                onClick={() => (setErrorCancelar(null), setCancelar(true))}
              >
                Cancelar pedido
              </Boton>
            ) : p.estado === 'por_aprobar' && !p.chat ? (
              <p className="text-center text-[15px] text-stone-600">
                Para cancelarlo, comunicate con la distribuidora.
              </p>
            ) : null}
          </div>

          <HojaInferior
            abierta={cancelar}
            onCerrar={() => setCancelar(false)}
            titulo={`¿Cancelar el pedido ${p.numero_visible}?`}
          >
            <p className="text-[17px] text-stone-700">
              La distribuidora no lo va a preparar. Si te arrepentís, tenés que hacer uno nuevo.
            </p>
            {errorCancelar ? (
              <p role="alert" className="mt-3 text-[15px] font-medium text-rose-800">
                {errorCancelar}
              </p>
            ) : null}
            <div className="mt-5 flex flex-col gap-3">
              <Boton
                variante="peligro"
                bloque
                tamano="lg"
                icono="cancel"
                cargando={cancelando}
                onClick={() => void confirmarCancelacion()}
              >
                Sí, cancelar el pedido
              </Boton>
              <Boton variante="secundario" bloque tamano="lg" onClick={() => setCancelar(false)}>
                No, dejarlo como está
              </Boton>
            </div>
          </HojaInferior>
          {volver.hoja}
        </div>
      )}
    </Pantalla>
  );
}

function textoDeEstado(p: PedidoDetalle, zona: string | undefined, empresa: string): string {
  switch (p.seguimiento.clave) {
    case 'por_aprobar':
      return `${empresa} todavía no lo confirmó. Te avisamos acá apenas lo haga.`;
    case 'confirmado':
      return `${empresa} lo confirmó. Te llega ${fechaEntrega(p.fecha_entrega, zona)}.`;
    case 'preparado':
      return 'Ya está preparado y cargado para salir.';
    case 'en_camino':
      return 'Tu pedido está en camino. Tené a mano la forma de pago.';
    case 'entregado':
      return 'Llegó todo. ¡Gracias por tu pedido!';
    case 'entregado_parcial':
      return 'Llegó una parte. Abajo ves qué llegó y qué volvió.';
    case 'no_entregado':
      return 'No se pudo entregar. La distribuidora lo va a reprogramar o se va a comunicar con vos.';
    case 'anulado':
      return 'La distribuidora anuló este pedido. Consultá con ellos.';
    case 'rechazado':
      return 'No se va a preparar. Si necesitás esos productos, hacé un pedido nuevo o consultá con la distribuidora.';
    case 'cancelado':
      return 'Lo cancelaste. No se va a preparar.';
    default:
      return p.seguimiento.etiqueta;
  }
}

function Dato({ icono, titulo, children }: { icono: string; titulo: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 p-4">
      <Icono nombre={icono} tamano={26} className="mt-0.5 shrink-0 text-primary-700" />
      <div className="min-w-0">
        <p className="text-[15px] text-stone-600">{titulo}</p>
        <p className="text-[17px] font-semibold break-words text-stone-900">{children}</p>
      </div>
    </div>
  );
}

function FilaItem({ i }: { i: ItemDetalle }) {
  const parcial = i.entregados !== null && (i.entregados < i.bultos || (i.devueltos ?? 0) > 0);
  return (
    <li className="flex gap-3 px-4 py-3.5">
      <Miniatura nombre={i.nombre} categoria={i.categoria} fotoMini={i.foto_mini_url} tamano="sm" />
      <div className="min-w-0 flex-1">
        <p className="text-[17px] leading-snug font-semibold text-stone-900">{i.nombre}</p>
        <p className="text-[15px] text-stone-600">
          {presentacion(i.unidad, i.unidades_por_bulto)}
          {i.precio_bulto !== null ? ` · ${formatoGs(i.precio_bulto)} por ${nombreUnidad(i.unidad)}` : ''}
        </p>
        <div className="mt-1 flex items-baseline justify-between gap-2">
          <span className="text-[17px] font-medium">{cantidadEnUnidad(i.bultos, i.unidad)}</span>
          {i.subtotal !== null ? (
            <span className="text-[17px] font-bold tabular-nums">{formatoGs(i.subtotal)}</span>
          ) : null}
        </div>
        {i.entregados !== null ? (
          <p
            className={cx(
              'mt-1.5 flex flex-wrap gap-x-3 text-[15px] font-medium',
              parcial ? 'text-amber-900' : 'text-emerald-800',
            )}
          >
            <span className="inline-flex items-center gap-1">
              <Icono nombre={parcial ? 'rule' : 'check_circle'} tamano={18} />
              {parcial ? `Llegaron ${i.entregados} de ${i.bultos}` : 'Llegó todo'}
            </span>
            {i.devueltos ? <span>Volvieron {plural(i.devueltos, 'bulto', 'bultos')}</span> : null}
          </p>
        ) : null}
      </div>
    </li>
  );
}
